// Client-side head manager. RouteTracker (App.js) calls applyRouteSeo on every
// navigation; pages with data-driven metadata (blog posts, public listings) call
// it again with overrides once their data arrives, and use the exported helpers
// for JSON-LD and extra tags.
//
// Everything here runs only in browsers that execute JavaScript. Crawlers that do
// not (OpenAI, Anthropic, Perplexity, social scrapers) see public/index.html only.

import { formatPriceCAD, isRentListing, unitFirstAddress } from './propertyUtils';

// Titles and descriptions mirror public/content/pages.json, which the server
// renders from; utils/seo.test.js fails when they differ.
export const DEFAULT_TITLE = 'KeyTa | AI Real Estate Platform';
export const DEFAULT_DESCRIPTION =
  'KeyTa is an AI real estate platform for home sellers, buyers, real estate agents and lawyers.';
const DEFAULT_OG_IMAGE = '/og-default.png';
const SITE_NAME = 'KeyTa';
const CITY_PAGE_RE = /^\/properties\/([a-z0-9-]+)$/;

const INDEXABLE_ROBOTS = 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1';
// noarchive keeps private pages out of Bing Copilot answers as well as the index.
const NOINDEX_ROBOTS = 'noindex,nofollow,noarchive';

const INDEXABLE_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/about\/?$/,
  /^\/market\/?$/,
  /^\/faq\/?$/,
  /^\/contact\/?$/,
  /^\/privacy-policy\/?$/,
  /^\/terms-of-service\/?$/,
  /^\/properties\/?$/,
  /^\/properties\/[a-z0-9-]+\/?$/, // MLS listings by city; server-rendered from the same slug rule
  /^\/properties-map\/?$/,
  /^\/property\/[^/]+\/?$/, // active MLS listings; PropertyDetail sets per-listing tags
  /^\/blog\/?$/,
  /^\/blog\/[^/]+\/?$/,
  /^\/[^/]+\/[^/]+\/[^/]+\/home\/[^/]+\/?$/,
];

export const TITLE_BY_PATH = {
  '/': 'KeyTa | AI Real Estate Platform for Buying and Selling Homes',
  '/about': 'About KeyTa | AI for Buyers, Sellers, Agents and Lawyers',
  '/faq': 'KeyTa FAQ | Fees, Selling, Buying and Closing',
  '/blog': 'KeyTa Blog | Guides to Buying, Selling and Closing a Home',
  '/properties': 'Homes for Sale Near You | KeyTa',
  '/market': 'Real Estate Market Insights | KeyTa',
  '/contact': 'Contact KeyTa | Help for Buyers, Sellers, Agents and Lawyers',
  '/privacy-policy': 'Privacy Policy | KeyTa',
  '/terms-of-service': 'Terms of Service | KeyTa',
};

export const DESCRIPTION_BY_PATH = {
  '/': 'Sell your home for a flat fee, search homes for sale, and work with real estate agents and lawyers on one AI platform, from pricing and offers to closing.',
  '/about': 'KeyTa brings AI pricing, listing, offer and closing tools to home sellers, buyers, real estate agents and lawyers, with licensed professionals for legal steps.',
  '/faq': "Answers about KeyTa's flat selling fee, listing and offers, buying a home, and how real estate agents and lawyers work with KeyTa.",
  '/blog': 'Practical guides to selling and buying a home: pricing, commissions, offers, legal steps and closing costs.',
  '/properties': "Search homes for sale near you with photos, prices and details, and ask KeyTa's AI assistant about any listing.",
  '/market': 'Local housing market updates, trends and pricing insights for smarter buying and selling decisions.',
  '/contact': 'Contact the KeyTa team for help selling or buying a home, or about working with KeyTa as a real estate agent or lawyer.',
  '/privacy-policy': 'How KeyTa collects, uses and protects personal information.',
  '/terms-of-service': 'The terms that govern use of the KeyTa platform.',
};

// Mirrors slugify_segment() in backend/utils/public_property_url.py, which builds
// the /properties/<city> hub URLs, so client and server agree on every slug.
export function slugifyCity(value) {
  return String(value || '')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Same strings as SeoRenderService._city_page and templates/seo/city.html, so the
// head tags and the h1 do not change on hydration.
export function cityPageSeo(city) {
  const heading = `Homes in ${city}, Ontario`;
  return {
    heading,
    title: `${heading} | KeyTa`,
    description: `Browse active MLS® listings for sale and rent in ${city}, Ontario with photos, prices and details, and ask KeyTa's AI assistant about any home.`,
  };
}

const LEASE_OUT = 'http://purl.org/goodrelations/v1#LeaseOut';

// Feed counts are decimals: 3.00 -> 3, 2.5 -> 2.5, missing or zero -> null.
const feedNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

// The seller allowed the listing online but not its address (the API blanks the street).
const addressWithheld = (property) =>
  property?.InternetAddressDisplayYN !== null && property?.InternetAddressDisplayYN !== undefined
  && !property.InternetAddressDisplayYN;

const homeInCity = (subtype, city) => {
  const kind = String(subtype || '').trim() || 'Home';
  const place = String(city || '').trim();
  return place ? `${kind} in ${place}` : kind;
};

const listingAddress = (property) => (addressWithheld(property)
  ? homeInCity(property.PropertySubType, property.City)
  : unitFirstAddress(property.UnparsedAddress || property.address, property.UnitNumber) || 'Property');

/**
 * What an MLS listing page is called: its h1, the start of its title and its
 * structured-data name. "1201 - 1 Bay St, Toronto C01", or "Condo Apartment in
 * Toronto C01" when the seller withheld the address. UnparsedAddress usually
 * already ends with the city, which is then not added twice.
 */
export function mlsListingName(property) {
  const address = listingAddress(property);
  const city = property.City || '';
  return city && address.toLowerCase().includes(city.toLowerCase()) ? address : `${address}${city ? `, ${city}` : ''}`;
}

/**
 * Title, description and structured data of an MLS listing page, by the same rules
 * as SeoRenderService._mls_listing: Google indexes the head as it stands after React
 * loads the listing, so it must match what the server sent. utils/seo.test.js runs
 * the server tests' cases (tests/test_seo_render.py).
 */
export function mlsListingSeo(property, listingKey, origin = getConfiguredSiteOrigin()) {
  // While a KeyTa property is live under this number, its KeyTa page is the home's one canonical
  // page (the API's keyta_public_path; the server render does the same).
  const canonical = property.keyta_public_path
    ? `${origin}${property.keyta_public_path}`
    : `${origin}/property/${listingKey}`;
  const city = property.City || '';
  const region = property.StateOrProvince || 'ON';
  const subtype = property.PropertySubType || '';
  const withheld = addressWithheld(property);
  const address = listingAddress(property);
  const name = mlsListingName(property);
  const lease = isRentListing(property);
  const price = feedNumber(property.ListPrice);
  const beds = feedNumber(property.BedroomsTotal);
  const baths = feedNumber(property.BathroomsTotalInteger);
  const facts = [
    price ? `${formatPriceCAD(price)}${lease ? '/month' : ''}` : null,
    beds ? `${beds} bed` : null,
    baths ? `${baths} bath` : null,
    subtype && !name.includes(subtype) ? subtype : null,
  ].filter(Boolean);
  const title = `${name} | ${facts.length ? `${facts.join(' · ')} | ` : ''}KeyTa`;
  const place = withheld && city ? `in ${city}` : `at ${name}`;
  const description = String(property.PublicRemarks || '').trim().slice(0, 158)
    || `${subtype || 'Home'} for ${lease ? 'lease' : 'sale'} ${place}, ${region}.`;
  const images = (property.Media || []).map((item) => item?.MediaURL).filter(Boolean);
  const dateOnly = (value) => (value ? String(value).slice(0, 10) : undefined);
  const citySlug = slugifyCity(city);

  return {
    name,
    title,
    description,
    canonical,
    image: images[0] || '',
    listing: {
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      '@id': `${canonical}#listing`,
      url: canonical,
      name,
      description,
      datePosted: dateOnly(property.OriginalEntryTimestamp),
      dateModified: dateOnly(property.ModificationTimestamp),
      image: images.length ? images.slice(0, 10) : undefined,
      // A lease is offered for rent, priced per month (GoodRelations LeaseOut, UN/CEFACT MON).
      offers: price ? {
        '@type': 'Offer',
        price,
        priceCurrency: 'CAD',
        businessFunction: lease ? LEASE_OUT : undefined,
        priceSpecification: lease
          ? { '@type': 'UnitPriceSpecification', price, priceCurrency: 'CAD', unitCode: 'MON', unitText: 'month' }
          : undefined,
        availability: 'https://schema.org/InStock',
        url: canonical,
      } : undefined,
      about: {
        '@type': 'Residence',
        name: address,
        numberOfBedrooms: beds || undefined,
        numberOfBathroomsTotal: baths || undefined,
        address: {
          '@type': 'PostalAddress',
          streetAddress: withheld ? undefined : address,
          addressLocality: city || undefined,
          addressRegion: region,
          postalCode: property.PostalCode || undefined,
          addressCountry: 'CA',
        },
      },
    },
    // Same ids and items as the server render.
    breadcrumbs: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: 'Find Properties', item: `${origin}/properties` },
        ...(citySlug ? [{ '@type': 'ListItem', position: 3, name: city, item: `${origin}/properties/${citySlug}` }] : []),
        { '@type': 'ListItem', position: citySlug ? 4 : 3, name, item: canonical },
      ],
    },
  };
}

function cityNameFromSlug(slug) {
  return slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getConfiguredSiteOrigin() {
  const envOrigin =
    process.env.REACT_APP_PUBLIC_SITE_URL ||
    process.env.REACT_APP_WEB_URL ||
    process.env.REACT_APP_FRONTEND_URL ||
    '';

  const normalizedEnvOrigin = envOrigin.trim().replace(/\/+$/, '');
  if (normalizedEnvOrigin) {
    return normalizedEnvOrigin;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return '';
}

function normalizePath(pathname = '') {
  if (!pathname || pathname === '/') return '/';
  const cleaned = pathname.replace(/\/+$/, '');
  return cleaned || '/';
}

export function isIndexablePath(pathname = '') {
  const normalizedPath = normalizePath(pathname);
  return INDEXABLE_ROUTE_PATTERNS.some((pattern) => pattern.test(normalizedPath));
}

export function toAbsoluteUrl(value) {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  const origin = getConfiguredSiteOrigin();
  return `${origin}${value.startsWith('/') ? '' : '/'}${value}`;
}

// Images the site publishes must be served from the site itself: a cover
// hot-linked from another site is someone else's photo and shows that site in
// KeyTa's search and link previews. Returns the URL when it is a root-relative
// path or on the site's own host, else ''. Mirrors utils/site_url.py::is_own_url.
export function ownImageUrl(value) {
  const url = String(value || '').trim();
  if (!url) return '';
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  try {
    const parsed = new URL(url);
    if (!/^https?:$/i.test(parsed.protocol)) return '';
    const ownHosts = [getConfiguredSiteOrigin(), typeof window !== 'undefined' ? window.location?.origin : '']
      .filter(Boolean)
      .map((origin) => new URL(origin).host.toLowerCase());
    return ownHosts.includes(parsed.host.toLowerCase()) ? url : '';
  } catch {
    return '';
  }
}

export function upsertMeta(key, content, attr = 'name') {
  if (typeof document === 'undefined' || !key || !content) return;

  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

export function removeMeta(key, attr = 'name') {
  if (typeof document === 'undefined' || !key) return;
  const tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (tag) tag.remove();
}

export function upsertCanonical(href) {
  if (typeof document === 'undefined' || !href) return;

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', href);
}

// One <script type="application/ld+json"> per id; pages remove theirs on unmount so
// structured data never leaks onto the next route.
export function upsertJsonLd(id, data) {
  if (typeof document === 'undefined' || !id || !data) return;

  let script = document.head.querySelector(`script[data-seo="${id}"]`);
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-seo', id);
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

export function removeJsonLd(id) {
  if (typeof document === 'undefined' || !id) return;
  const script = document.head.querySelector(`script[data-seo="${id}"]`);
  if (script) script.remove();
}

/**
 * Write title, description, robots, canonical, Open Graph and Twitter tags for a path.
 *
 * @param {string} pathname  route path (location.pathname)
 * @param {object} overrides optional { title, description, image, type, canonical, robots }
 */
// True while the document still carries the head the server rendered for this
// path: routers/render.py always writes a canonical link and the static shell
// has none. The first route then keeps the server's title, description and
// robots, so crawlers that run JavaScript index the same head as those that
// do not; later in-app navigations get the client head.
export function hasServerRenderedHead(pathname = '') {
  if (typeof document === 'undefined') return false;
  const href = document.head.querySelector('link[rel="canonical"]')?.getAttribute('href');
  if (!href) return false;
  try {
    return normalizePath(new URL(href, window.location.origin).pathname) === normalizePath(pathname);
  } catch {
    return false;
  }
}

export function applyRouteSeo(pathname = '', overrides = {}) {
  if (typeof document === 'undefined') return;

  const normalizedPath = normalizePath(pathname);
  const origin = getConfiguredSiteOrigin();
  const canonicalUrl = overrides.canonical || `${origin}${normalizedPath}`;
  const indexable = isIndexablePath(normalizedPath);

  // City hub pages get a slug-based title until FindPropertiesPage resolves the exact city name.
  const cityMatch = CITY_PAGE_RE.exec(normalizedPath);
  const citySeo = cityMatch ? cityPageSeo(cityNameFromSlug(cityMatch[1])) : {};

  const title = overrides.title || TITLE_BY_PATH[normalizedPath] || citySeo.title || DEFAULT_TITLE;
  const description =
    overrides.description || DESCRIPTION_BY_PATH[normalizedPath] || citySeo.description || DEFAULT_DESCRIPTION;
  const robots = overrides.robots || (indexable ? INDEXABLE_ROBOTS : NOINDEX_ROBOTS);
  const image = toAbsoluteUrl(overrides.image || DEFAULT_OG_IMAGE);
  const ogType = overrides.type || 'website';

  document.title = title;
  upsertCanonical(canonicalUrl);

  upsertMeta('description', description, 'name');
  upsertMeta('robots', robots, 'name');

  upsertMeta('og:site_name', SITE_NAME, 'property');
  upsertMeta('og:locale', 'en_CA', 'property');
  upsertMeta('og:type', ogType, 'property');
  upsertMeta('og:title', title, 'property');
  upsertMeta('og:description', description, 'property');
  upsertMeta('og:url', canonicalUrl, 'property');
  upsertMeta('og:image', image, 'property');
  // Same rule as the server: only the default image's size is known.
  if (image === toAbsoluteUrl(DEFAULT_OG_IMAGE)) {
    upsertMeta('og:image:width', '1200', 'property');
    upsertMeta('og:image:height', '630', 'property');
  } else {
    removeMeta('og:image:width', 'property');
    removeMeta('og:image:height', 'property');
  }

  upsertMeta('twitter:card', 'summary_large_image', 'name');
  upsertMeta('twitter:title', title, 'name');
  upsertMeta('twitter:description', description, 'name');
  upsertMeta('twitter:image', image, 'name');
}
